import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportsPaymentSummaryData } from "src/features/Reports/types/report.types";
import { REPORTS_PAYMENT_SUMMARY } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportBarChart } from "src/components/BarChart/BarChart";
import { useMobileNavBreakpoint } from "src/hooks/useMobileNavBreakpoint";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";

export default function PaymentSummaryReportPage() {
  const { hasRestaurant } = useAuth();
  const isMobile = useMobileNavBreakpoint();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const input = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const { data, loading, error } = useReportQuery<ReportsPaymentSummaryData>(
    REPORTS_PAYMENT_SUMMARY,
    "reportsPaymentSummary",
    input
  );

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const totalAmountCents = useMemo(
    () => rows.reduce((sum, r) => sum + (r.totalAmountCents ?? 0), 0),
    [rows]
  );
  const barData = useMemo(() => {
    return rows.map((row) => {
      const rawName = (row.paymentMethod ?? "Other").trim().toLowerCase();
      const name = rawName === "card" ? "Card" : rawName === "other" ? "Other" : rawName.charAt(0).toUpperCase() + rawName.slice(1);
      const percent = row.percentOfTotal != null ? row.percentOfTotal : 0;
      return { name, value: percent, amount: row.totalAmountCents != null ? row.totalAmountCents / 100 : 0 };
    });
  }, [rows]);
  const paymentBarColors = ["#8fbfa8", "#94a3b8"];

  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      error={error}
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <>
          {barData.length > 0 && (
            <div className="rounded-[14px] bg-white p-4 border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)] mb-4">
              <p className="text-[14px] font-medium text-[#374151] mb-1">Payment mix</p>
              <p className="text-[22px] font-semibold text-[#111827] mb-3">
                {formatCentsToDollarsWithDollarSign(totalAmountCents)}
                <span className="text-sm font-normal text-[#6b7280] ml-2">Total payments</span>
              </p>
              <div className="h-[120px]">
                <ReportBarChart
                  data={barData}
                  dataKey="value"
                  nameKey="name"
                  horizontal
                  isShowDollarSign={false}
                  valueSuffix="%"
                  colors={paymentBarColors}
                  yAxisWidth={isMobile ? 84 : 168}
                />
              </div>
            </div>
          )}
          <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-x-auto">
            <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr>
                  <th className={`pl-4 pr-2 ${headerCellClass}`}>Payment Method</th>
                  <th className={`px-2 ${headerCellClass}`}>Count</th>
                  <th className={`px-2 ${headerCellClass}`}>Total Amount</th>
                  <th className={`px-2 ${headerCellClass}`}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-[#6b7280] text-sm">
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={i}
                      className="border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB]"
                    >
                      <td className="py-2 pl-4 pr-2">
                        {(() => {
                          const raw = (row.paymentMethod ?? "").trim().toLowerCase();
                          if (raw === "card") return "Card";
                          if (raw === "other") return "Other";
                          return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "—";
                        })()}
                      </td>
                      <td className="py-2 px-2">{row.count ?? "—"}</td>
                      <td className="py-2 px-2">{formatCentsToDollarsWithDollarSign(row.totalAmountCents ?? 0)}</td>
                      <td className="py-2 px-2">{row.percentOfTotal != null ? `${row.percentOfTotal.toFixed(1)}%` : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </ReportPageLayout>
  );
}
